import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecordsApi } from "../../lib/api";
import { Card } from "../../components/ui/Card";
import { LoadingOverlay } from "../../components/ui/LoadingOverlay";
import { Loader2, X, Link as LinkIcon, Book, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import type { DeltaStatic, Sources } from "quill";
import Quill from "quill";
import { WritingAssistant } from "../../components/admin/WritingAssistant";

interface Reference {
  reference_type_id: number;
  reference: string;
}

interface CreateRecordPayload {
  title: string;
  content: string;
  formatted_content: DeltaStatic;
  meta?: string;
  references: Reference[];
}

interface FormData extends CreateRecordPayload {}

export function CreateRecordPage() {
  const navigate = useNavigate();
  const { createRecord, getReferenceTypes } = useRecordsApi();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newReference, setNewReference] = useState("");
  const [selectedReferenceTypeId, setSelectedReferenceTypeId] = useState<
    number | null
  >(null);
  const quillRef = useRef<ReactQuill>(null);
  const [formData, setFormData] = useState<FormData>({
    title: "",
    content: "",
    formatted_content: new Quill(document.createElement("div")).getContents(),
    meta: "",
    references: [],
  });
  const [showWritingAssistant, setShowWritingAssistant] = useState(false);

  const { data: referenceTypes, isLoading: isLoadingReferenceTypes } = useQuery(
    {
      queryKey: ["reference-types"],
      queryFn: async () => {
        const types = await getReferenceTypes();
        return types.map((type) => ({ ...type, id: parseInt(type.id, 10) }));
      },
    }
  );

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createRecord({
        ...formData,
        references: formData.references.map((ref) => ({
          reference_type_id: ref.reference_type_id,
          reference: ref.reference,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin-records"] });
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      await queryClient.invalidateQueries({ queryKey: ["analytics"] });
      navigate("/admin/records");
    } catch (error) {
      console.error("Failed to create record:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (
    content: string,
    delta: DeltaStatic,
    source: Sources,
    editor: ReactQuill.UnprivilegedEditor
  ) => {
    setFormData((prev) => ({
      ...prev,
      content: editor.getText(), // Get plain text
      formatted_content: editor.getContents(), // Get Delta format
    }));
  };

  /**
   * Handle accepting enhanced text from WritingAssistant.
   * The text parameter now contains HTML content from the LLM.
   */
  const handleAcceptEnhanced = (htmlContent: string, formattedContent?: DeltaStatic) => {
    // Create a temporary Quill instance to convert HTML to Delta if needed
    if (formattedContent) {
      // Use Delta format directly if available
      // Extract plain text from Delta
      const tempContainer = document.createElement('div');
      const tempQuill = new Quill(tempContainer);
      tempQuill.setContents(formattedContent);
      const plainText = tempQuill.getText().trim();

      setFormData((prev) => ({
        ...prev,
        content: plainText,
        formatted_content: formattedContent,
      }));
    } else if (htmlContent) {
      // Convert HTML to Delta format using a temporary Quill instance
      const tempContainer = document.createElement('div');
      const tempQuill = new Quill(tempContainer);
      tempQuill.clipboard.dangerouslyPasteHTML(0, htmlContent);
      const newDelta = tempQuill.getContents();
      const plainText = tempQuill.getText().trim();

      setFormData((prev) => ({
        ...prev,
        content: plainText,
        formatted_content: newDelta,
      }));
    }
    setShowWritingAssistant(false);
  };

  /**
   * Handle using original text (close assistant without changes).
   */
  const handleUseOriginal = () => {
    setShowWritingAssistant(false);
  };

  const handleAddReference = (e?: React.KeyboardEvent<HTMLInputElement>) => {
    // Only proceed if it's a button click (no event) or Enter key press
    if (e && e.key !== "Enter") {
      return;
    }

    if (e?.key === "Enter") {
      e.preventDefault();
    }

    if (newReference.trim() && selectedReferenceTypeId !== null) {
      const value = newReference.trim();
      const selectedType = referenceTypes?.find(
        (type) => type.id === selectedReferenceTypeId
      );

      if (selectedType?.name === "Link" && !isValidUrl(value)) {
        console.log("Invalid URL detected");
        return; // Don't add invalid URLs
      }

      setFormData((prev) => ({
        ...prev,
        references: [
          ...prev.references,
          { reference_type_id: selectedReferenceTypeId, reference: value },
        ],
      }));
      setNewReference("");
    }
  };

  const handleRemoveReference = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index),
    }));
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getPreview = (reference: Reference) => {
    const refType = referenceTypes?.find(
      (type) => type.id === reference.reference_type_id
    );

    if (refType?.name === "Link") {
      try {
        const urlObj = new URL(reference.reference);
        return (
          urlObj.hostname +
          urlObj.pathname.slice(0, 20) +
          (urlObj.pathname.length > 20 ? "..." : "")
        );
      } catch {
        return reference.reference;
      }
    }
    return reference.reference;
  };

  const getTypeIcon = (typeId: number) => {
    const refType = referenceTypes?.find((type) => type.id === typeId);
    switch (refType?.name) {
      case "Link":
        return <LinkIcon className="h-3 w-3" />;
      case "Book":
        return <Book className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <>
      <LoadingOverlay
        isVisible={isSubmitting}
        message="Création du document"
        subMessage="Génération des embeddings et du résumé. Cela peut prendre quelques instants..."
      />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Create New Record
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Add a new document to your knowledge base
            </p>
          </div>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="space-y-8">
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700"
                >
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="content"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Content
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowWritingAssistant(!showWritingAssistant)}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="h-4 w-4" />
                    {showWritingAssistant ? "Hide AI Assistant" : "Enhance with AI"}
                    {showWritingAssistant ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Writing Assistant Panel */}
                {showWritingAssistant && (
                  <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50/50 p-4">
                    <WritingAssistant
                      question={formData.title}
                      initialText={formData.content}
                      initialFormattedContent={formData.formatted_content}
                      onAcceptEnhanced={handleAcceptEnhanced}
                      onUseOriginal={handleUseOriginal}
                      disabled={isSubmitting}
                      originalLabel="Your Content"
                      enhancedLabel="Enhanced Content"
                    />
                  </div>
                )}

                {/* Standard Editor */}
                {!showWritingAssistant && (
                  <div className="mt-1">
                    <ReactQuill
                      ref={quillRef}
                      value={formData.formatted_content}
                      onChange={handleEditorChange}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      readOnly={isSubmitting}
                    />
                  </div>
                )}
              </div>

              <div className="pt-4">
                <label
                  htmlFor="references"
                  className="block text-sm font-medium text-gray-700"
                >
                  References
                </label>
                <div className="mt-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedReferenceTypeId?.toString() || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedReferenceTypeId(
                          value ? parseInt(value, 10) : null
                        );
                      }}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={isLoadingReferenceTypes || isSubmitting}
                    >
                      <option value="">Select type...</option>
                      {referenceTypes?.map((type) => {
                        return (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        );
                      })}
                    </select>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={newReference}
                        onChange={(e) => setNewReference(e.target.value)}
                        onKeyDown={handleAddReference}
                        placeholder={
                          selectedReferenceTypeId !== null
                            ? `${
                                referenceTypes?.find(
                                  (t) => t.id === selectedReferenceTypeId
                                )?.name === "Link"
                                  ? "🔗"
                                  : "📚"
                              } Enter ${
                                referenceTypes?.find(
                                  (t) => t.id === selectedReferenceTypeId
                                )?.name
                              } and press Enter`
                            : "Select a reference type first"
                        }
                        disabled={
                          selectedReferenceTypeId === null || isSubmitting
                        }
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-12 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddReference()}
                        disabled={
                          selectedReferenceTypeId === null ||
                          !newReference.trim() ||
                          isSubmitting
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-purple-600 p-1 text-white hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="block h-4 w-4 font-semibold leading-4">
                          +
                        </span>
                      </button>
                    </div>
                  </div>
                  {formData.references.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.references.map((ref, index) => {
                        const refType = referenceTypes?.find(
                          (type) => type.id === ref.reference_type_id
                        );
                        return (
                          <div
                            key={index}
                            className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                              refType?.name === "Link"
                                ? "bg-purple-50 text-purple-700"
                                : refType?.name === "Book"
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-blue-50 text-blue-700"
                            }`}
                          >
                            {getTypeIcon(ref.reference_type_id)}
                            <span>{getPreview(ref)}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveReference(index)}
                              disabled={isSubmitting}
                              className="ml-1 rounded-full p-0.5 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/records")}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Record
              </button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
